import { useState, useEffect } from "react";
import {
  Box,
  MenuItem,
  Stack,
  Select,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { setDashboardHeader, clearDashboardHeader } from "../../store/reducers/sideMenu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  Wrapper,
  Calender,
} from "../../components";

import { DashboardContent } from "../../components/dashboard";

import {
  Logs,
  companyDashboardDailyUsageTrendData,
  companyDashboardSummary,
  companyDashboardApiWiseUsageData,
  companyDashboardRecentApiCallData,
} from "../../services";
import { useDispatch } from "react-redux";
import { setActiveMenuLabel } from "../../store/reducers/sideMenu";

dayjs.extend(relativeTime);

const normalizeDailyUsageTrendData = (rawData) => {
  const source =
    rawData?.graphData ??
    rawData?.data ??
    rawData;

  if (!Array.isArray(source)) {
    return { graphData: [] };
  }

  const graphData = source
    .map((item) => {
      const day =
        item?.day ??
        item?.date ??
        item?.label ??
        item?.name ??
        item?._id;

      const totalCalls =
        item?.totalCalls ??
        item?.total_calls ??
        item?.total ??
        item?.totalRequests ??
        item?.total_requests ??
        0;

      const successCalls =
        item?.successCalls ??
        item?.success_calls ??
        item?.success ??
        item?.successRequests ??
        item?.success_requests ??
        0;

      return {
        day,
        totalCalls: Number(totalCalls) || 0,
        successCalls: Number(successCalls) || 0,
      };
    })
    .filter((item) => item.day !== undefined && item.day !== null);

  return { graphData };
};

/* ===================== DASHBOARD ===================== */
export function Dashboard({companyName: propCompanyName = '', onDatesChange}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  const formatCompanyName = (name = "") =>
    name
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ");
  

  const { companyName } = useParams();
  const [searchParams] = useSearchParams();
  const decodedCompanyName = propCompanyName ? propCompanyName : decodeURIComponent(companyName) || null;
  const formattedCompanyName = formatCompanyName(decodedCompanyName);

  const { state } = useLocation();
  const status = state?.status;
  const apisSubscribed = state?.apisSubscribed;

  const getStatusStyles = (status) => {
    const normalized = status?.toLowerCase();
  
    if (normalized === "active") {
      return {
        bg: "#DCFCE7",
        color: "#166534",
      };
    }
  
    return {
      bg: "#E5E7EB",
      color: "#374151",
    };
  };
 
  const statusStyles = getStatusStyles(status);
  /* ===================== DATE FILTER ===================== */
  const [days, setDays] = useState("7");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);

  const currentDate = dayjs();
  const sevenDaysBack = currentDate.subtract(7, "day");

  const defaultDateRange = [
    sevenDaysBack.startOf("day"),
    currentDate.endOf("day"),
  ];

  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format("YYYY-MM-DD"),
    endDate: defaultDateRange[1].format("YYYY-MM-DD"),
  });

  const handleDaysChange = (value) => {
    setDays(value);

    if (value === "custom") {
      setCalendarKey((prev) => prev + 1);
      setCalendarOpen(true);
      return;
    }

    setCalendarOpen(false);

    const endDate = dayjs();
    const startDate = endDate.subtract(Number(value), "day");
    const newRange = [startDate.startOf("day"), endDate.endOf("day")];

    setDateRangeValue(newRange);
    const newDates = {
      startDate: newRange[0].format("YYYY-MM-DD"),
      endDate: newRange[1].format("YYYY-MM-DD"),
    };
    setDashboardDates(newDates);
    onDatesChange?.(newDates);
  };

  const handleChangeCalender = (dates) => {
    if (!dates) return;

    setDateRangeValue(dates);
    const newDates = {
      startDate: dates[0].format("YYYY-MM-DD"),
      endDate: dates[1].format("YYYY-MM-DD"),
    };
    setDashboardDates(newDates);
    onDatesChange?.(newDates);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (decodedCompanyName) {
      dispatch(setActiveMenuLabel(""));
    }
  }, [decodedCompanyName, dispatch]);

  /* ===================== STATE ===================== */
  const [companyDashboardSummaryData, setCompanyDashboardSummaryData] = useState(null);
  const [dailyUsageTrendData, setDailyUsageTrendData] = useState(null);
  const [apiWiseUsageData, setApiWiseUsageData] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [apiNameFilter, setApiNameFilter] = useState(() => searchParams.get("apiName") || null);

  const [recentApiCall, setRecentApiCall] = useState([]);
  const [totalRecentApiCallCount, setRecentApiCallCount] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [dailyUsageLoading, setDailyUsageLoading] = useState(true);
  const [apiWiseLoading, setApiWiseLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  /* ===================== API CALLS ===================== */
  useEffect(() => {
    if (!jwtToken) return;

    setSummaryLoading(true);
    setDailyUsageLoading(true);
    setApiWiseLoading(true);

    companyDashboardSummary(jwtToken, decodedCompanyName, dashboardDates)
      .then((res) => res?.status === 200 && setCompanyDashboardSummaryData(res.data))
      .catch((e) => console.error("Summary API error:", e))
      .finally(() => setSummaryLoading(false));

    companyDashboardDailyUsageTrendData(jwtToken, decodedCompanyName, dashboardDates)
      .then((res) => res?.status === 200 && setDailyUsageTrendData(normalizeDailyUsageTrendData(res.data)))
      .catch((e) => console.error("Daily usage API error:", e))
      .finally(() => setDailyUsageLoading(false));

    companyDashboardApiWiseUsageData(jwtToken, decodedCompanyName, dashboardDates)
      .then((res) => res?.status === 200 && setApiWiseUsageData(res.data))
      .catch((e) => console.error("API-wise usage error:", e))
      .finally(() => setApiWiseLoading(false));

  }, [jwtToken, decodedCompanyName, dashboardDates]);
  
  useEffect(() => {
    if (!jwtToken) return;
  
    const fetchRecentApiCalls = async () => {
      try {
        setTableLoading(true);
  
        const res = await companyDashboardRecentApiCallData(jwtToken, decodedCompanyName, dashboardDates, page, rowsPerPage, apiNameFilter);
  
        if (res?.status === 200) {
          setRecentApiCall(res.data?.apiCalls || []);
          setRecentApiCallCount(
            res.data?.pagination?.totalRecords || 0
          );
        }
      } catch (error) {
        console.error("Recent API calls API error:", error);
      } finally {
        setTableLoading(false);
      }
    };
    fetchRecentApiCalls();
  }, [jwtToken, decodedCompanyName, dashboardDates, page, rowsPerPage, apiNameFilter]);  

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!formattedCompanyName) return;
  
    dispatch(
      setDashboardHeader({
        companyName: formattedCompanyName,
        status,
        apisSubscribed,
      })
    );
  
    return () => {
      dispatch(clearDashboardHeader()); 
    };
  }, [formattedCompanyName, status, apisSubscribed, dispatch]);
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", 
    });
  }, []);

  const handleApiNameClick = (apiName) => {
    setApiNameFilter(apiName || null);
    setPage(1);
  };

  const CONTROL_HEIGHT = 38;

  /* ===================== UI ===================== */
  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
       {/* Back */}
       
         <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
          {/* Left */}
          <Box
            onClick={() => navigate("/customer-analytics")}
            sx={{
              display: "inline-flex",     
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.4,
              width: "fit-content",        
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              fontSize: "13px",
              fontWeight: 500,
              color: "#475569",
              backgroundColor: "#FFFFFF",
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "#F8FAFC",
                color: "#020817",
              },
            }}
          >
          <ArrowBackIcon fontSize="small" />
            Back to All Customers
          </Box>

          {/* DATE FILTER */}
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Select
                size="small"
                value={days}
                onChange={(e) => handleDaysChange(e.target.value)}
                sx={{
                  borderRadius: 3,
                  fontSize: "14px",
                  height: CONTROL_HEIGHT,
                  bgcolor: "#fff",
                }}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>

              {calendarOpen && (
                <Box key={calendarKey}>
                  <Calender
                    handleChange={handleChangeCalender}
                    defaultValue={defaultDateRange}
                    value={dateRangeValue}
                    maxDate={new Date()}
                  />
                </Box>
              )}
            </Box>
        </Box>
        
        {/* CONTENT */}
        <DashboardContent
          summaryLoading={summaryLoading}
          dailyUsageLoading={dailyUsageLoading}
          apiWiseLoading={apiWiseLoading}
          tableLoading={tableLoading}
          recentApiCall={recentApiCall}
          totalRecentApiCallCount={totalRecentApiCallCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          apiWiseUsageData={apiWiseUsageData}
          dailyUsageTrendData={dailyUsageTrendData}
          companyDashboardSummaryData={companyDashboardSummaryData}
          onApiNameClick={handleApiNameClick}
          apiNameFilter={apiNameFilter}
        />
      </Stack>
    </Wrapper>
  );
} 
 
